import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { Analytics } from '@vercel/analytics/react';

import Header from './components/Header';
import Hero from './components/Hero';
import FeaturedProducts from './components/FeaturedProducts';
import Products from './components/Products';
import About from './components/About';
import Footer from './components/Footer';
import ArtOverlay from './components/ArtOverlay';
import ComingSoonModal from './components/ComingSoonModal';
import FloatingBubbles from './components/FloatingBubbles';
import FloatingSocialBubbles from './components/FloatingSocialBubbles';
import GameOverlay from './components/GameOverlay';
import CandlesOverlay from './components/CandlesOverlay';
import MugOverlay from './components/MugOverlay';

import CartPreview from './components/CartPreview';
import StripeCheckout from './components/StripeCheckout';

import ScrollToTop from './components/ScrollToTop';
import AboutPage from './components/pages/AboutPage';
import ShoppingPage from './components/pages/ShoppingPage';
import FarmApparelPage from './components/pages/FarmApparelPage';
import AppstorePage from './components/pages/AppstorePage';
import WebGamesPage from './components/pages/WebGamesPage';
import StateStorePage from './components/pages/StateStorePage';

import './App.css';

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
`;

function App() {
  const [artOverlayOpen, setArtOverlayOpen] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [gameOverlayOpen, setGameOverlayOpen] = useState(false);
  const [candlesOverlayOpen, setCandlesOverlayOpen] = useState(false);
  const [mugOverlayOpen, setMugOverlayOpen] = useState(false);

  const [cart, setCart] = useState([]);
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleArtClick = () => {
    setArtOverlayOpen(true);
  };

  const handleArtClose = () => {
    setArtOverlayOpen(false);
  };

  const handleShowComingSoon = () => {
    setComingSoonOpen(true);
  };

  const handleComingSoonClose = () => {
    setComingSoonOpen(false);
  };

  const handleGameOpen = () => {
    setGameOverlayOpen(true);
  };

  const handleGameClose = () => {
    setGameOverlayOpen(false);
  };

  const handleCandleClick = () => {
    setCandlesOverlayOpen(true);
  };

  const handleCandlesClose = () => {
    setCandlesOverlayOpen(false);
  };

  const handleMugClick = () => {
    setMugOverlayOpen(true);
  };

  const handleMugClose = () => {
    setMugOverlayOpen(false);
  };




  // Cart management functions
  // eslint-disable-next-line no-unused-vars
  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateCartItem = (itemId, newQuantity) => {
    setCart(cart.map(item =>
      item.id === itemId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const handleCartClick = () => {
    setCartPreviewOpen(true);
  };

  const handleBackToStore = () => {
    setCartPreviewOpen(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <AppContainer>
        <Header
          onArtClick={handleArtClick}
          onCartClick={handleCartClick}
          onShowComingSoon={handleShowComingSoon}
          onGameOpen={handleGameOpen}
          cartItemCount={cart.reduce((total, item) => total + item.quantity, 0)}
        />
        <Routes>
          <Route path="/" element={
            <>
              <Hero />
              <FeaturedProducts />
              <Products />
              <About />
            </>
          } />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/chikin-lady-shopping" element={<ShoppingPage />} />
          <Route path="/farm-chicken-apparel" element={<FarmApparelPage />} />
          <Route path="/chikin-lady-appstore" element={<AppstorePage onGameOpen={handleGameOpen} />} />
          <Route path="/chikin-web-games" element={<WebGamesPage onGameOpen={handleGameOpen} />} />
          <Route path="/chicken-gifts-texas" element={<StateStorePage stateKey="texas" />} />
          <Route path="/chicken-gifts-california" element={<StateStorePage stateKey="california" />} />
          <Route path="/chicken-gifts-florida" element={<StateStorePage stateKey="florida" />} />
          <Route path="*" element={
            <>
              <Hero />
              <FeaturedProducts />
              <Products />
              <About />
            </>
          } />
        </Routes>
        <Footer onShowComingSoon={handleShowComingSoon} />

        <ArtOverlay
          isOpen={artOverlayOpen}
          onClose={handleArtClose}
        />

        <ComingSoonModal
          isOpen={comingSoonOpen}
          onClose={handleComingSoonClose}
        />



        <FloatingBubbles
          onCandleClick={handleCandleClick}
          onMugClick={handleMugClick}
          isMugOverlayOpen={mugOverlayOpen}
          isCandlesOverlayOpen={candlesOverlayOpen}
        />

        <FloatingSocialBubbles />

        {/* Cart Preview Overlay */}
        <CartPreview
          isOpen={cartPreviewOpen}
          onClose={() => setCartPreviewOpen(false)}
          cart={cart}
          onUpdateCart={updateCartItem}
          onRemoveFromCart={removeFromCart}
          onProceedToCheckout={() => setCheckoutOpen(true)}
          onBackToStore={handleBackToStore}
        />

        {/* Stripe Checkout Overlay */}
        <StripeCheckout
          isOpen={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          cart={cart}
          onUpdateCart={updateCartItem}
          onRemoveFromCart={removeFromCart}
        />

        <GameOverlay
          isOpen={gameOverlayOpen}
          onClose={handleGameClose}
        />

        <CandlesOverlay
          isOpen={candlesOverlayOpen}
          onClose={handleCandlesClose}
        />

        <MugOverlay
          isOpen={mugOverlayOpen}
          onClose={handleMugClose}
        />

        <Analytics />
      </AppContainer>
    </Router>
  );
}

export default App;
